import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useListArchieConversations } from "../../hooks/useArchieChat";
import { ArchieConversationSummary } from "../../types/archie/archie";

interface Props {
  customerId: string;
  userId: string;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const ArchieConversationHistory = ({
  customerId,
  userId,
  onSelectConversation,
  onNewConversation,
}: Readonly<Props>) => {
  const { data, isLoading, error } = useListArchieConversations(customerId, userId);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={onNewConversation}
          size="small"
        >
          New Chat
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Typography
            variant="body2"
            color="error"
            sx={{ p: 2, textAlign: "center" }}
          >
            Failed to load conversations.
          </Typography>
        )}

        {!isLoading && !error && data?.conversations.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            No past conversations yet.
          </Typography>
        )}

        {data && data.conversations.length > 0 && (
          <List disablePadding>
            {data.conversations.map((conv: ArchieConversationSummary) => (
              <ListItemButton
                key={conv.conversationId}
                onClick={() => onSelectConversation(conv.conversationId)}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  py: 1,
                  px: 2,
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      noWrap
                      sx={{ maxWidth: 300 }}
                    >
                      {conv.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeTime(conv.updatedAt)} &middot;{" "}
                      {conv.messageCount} messages
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ArchieConversationHistory;
